import { info } from '@ms/yammer-console-logging';
import { loadProtobufClientProperties } from '@ms/yammer-telemetry';
import { getTelemetryHostingConfig } from '@ms/yammer-telemetry-store';

import { getEtagFromStateOrPersistedState } from '../../domains/ecs/selectors';
import { getClientForwardExperiments } from '../../domains/experiments/selectors';
import { isFeatureRolloutEnabled } from '../../domains/featureRollouts/selectors';
import { getGraphqlHost } from '../../domains/session/selectors';
import type { YammerState } from '../../state/types';
import { addTreatmentsToRequest, getTreatmentsExtensionForGraphqlBody } from '../graphqlApiRequest';
import type { ApiHeaders, ApiMiddleware, GraphqlApiRequest, GraphqlOperation, ManagedApiRequest } from '../types';

import {
  GraphqlApiV5EndpointVersionNumber,
  isBackendGraphqlApiV5EndpointEnabledForOperation,
} from './graphqlApiV5Endpoint';

const getRequestHostOrDefault = ({ host }: ManagedApiRequest, state: YammerState) => host || getGraphqlHost(state);

const addCommonTreatmentsToRequestBody = (request: GraphqlApiRequest, state: YammerState): GraphqlApiRequest => {
  const treatments = getTreatmentsExtensionForGraphqlBody({
    state,
    featureRollouts: ['GraphqlForcedTracing'],
    experiments: [...getClientForwardExperiments(state), 'DisablePollPostType', 'HideUserGlobalToggle'],
  });

  return addTreatmentsToRequest({ request, treatments });
};

const getConditionalForcedTracingHeaders = (request: ManagedApiRequest, state: YammerState): ApiHeaders | undefined => {
  const { body } = request;
  const xRequestId = crypto.randomUUID();
  const headersWithXRequestId = {
    'X-Request-Id': xRequestId,
  };

  if (
    getTelemetryHostingConfig()?.hostingEnvironment !== 'dev' &&
    !isFeatureRolloutEnabled(state, 'GraphqlForcedTracing')
  ) {
    return headersWithXRequestId;
  }

  logForcedTraceHeaders(body as GraphqlOperation, xRequestId);

  return {
    ...headersWithXRequestId,
    // Header for forced tracing: https://github.com/openzipkin/b3-propagation#debug-flag-1
    'X-B3-Flags': '1',
  };
};

const getClientPropertiesHeaders = async (): Promise<ApiHeaders> => {
  const { getProtobufClientProperties } = await loadProtobufClientProperties();
  const clientProperties = await getProtobufClientProperties();

  return clientProperties ? { 'X-Analytics-Client-Properties': clientProperties } : {};
};

const getETagHeaders = async (state: YammerState): Promise<ApiHeaders | undefined> => {
  const etag = await getEtagFromStateOrPersistedState(state);

  return etag ? { 'X-ECS-ETag': etag } : undefined;
};

const logForcedTraceHeaders = ({ operationName, variables }: GraphqlOperation, xRequestId: string) => {
  info(`GraphQL ${operationName} ${xRequestId}
    Variables: ${JSON.stringify(variables) || '{}'}

    Downstream requests: https://lens.msftcloudes.com/v2/#/discover/query//results?datasource=(cluster:yammer.kusto.windows.net,database:Logs,datasourceType:Kusto)&runquery=1&plaintextquery=graphql_request_downstream_compare_v1(%22${xRequestId}%22%2C%20%22${xRequestId}%22%2C%20time(%227.00%3A00%3A00%22))%20%2F%2F%20Replace%20the%20second%20request%20ID%20to%20compare%20requests.%20%20Defaults%20to%20checking%20within%20the%20past%207d.%20%20There%20may%20be%20a%205-15%20minute%20lag%20before%20the%20data%20for%20a%20request%20ID%20will%20be%20ready%20in%20Kusto.%20Ensure%20the%20GraphqlForcedTracing%20flight%20is%20enabled%20or%20the%20request%20ID%20may%20not%20be%20found.%20
    Logs: https://lens.msftcloudes.com/v2/#/discover/query/GraphQL%20-%20By%20Request%20Id/results/table?_g=(ws:'2b557d89-2772-4080-966c-d13c4c0b490d')&args_x_request_id=${xRequestId}`);
};

const getOperationNameParameters = (request: ManagedApiRequest) => ({
  operationName: (request.body as GraphqlOperation).operationName,
});

const getGraphqlApiVnextParameters = (state: YammerState, request: ManagedApiRequest) => {
  const isBackendGraphqlApiV5EndpointEnabled = isBackendGraphqlApiV5EndpointEnabledForOperation(
    state,
    (request.body as GraphqlOperation).operationName
  );

  return isBackendGraphqlApiV5EndpointEnabled ? { apiVnext: GraphqlApiV5EndpointVersionNumber } : {};
};

const middleware: ApiMiddleware = async (request, state) => {
  const updatedRequest = {
    ...request,
    host: getRequestHostOrDefault(request, state),
    headers: {
      ...request.headers,
      ...getConditionalForcedTracingHeaders(request, state),
      ...(await getClientPropertiesHeaders()),
      ...(await getETagHeaders(state)),
    },
    query: {
      ...getOperationNameParameters(request),
      ...getGraphqlApiVnextParameters(state, request),
    },
  };

  return addCommonTreatmentsToRequestBody(updatedRequest as GraphqlApiRequest, state);
};

export default middleware;
